import * as React from 'react';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { FormControl, InputLabel, Select as MuiSelect, MenuItem, FormHelperText } from "@mui/material";

const renderOption = (item, indent) => (
    <MenuItem
        key={item.id}
        value={item.id}
        sx={{
            '&:hover': { backgroundColor: '#cccccc' },
            pl: indent ? 4 : 2,
        }}
    >
        {item.color ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                    sx={{
                        width: "16px",
                        height: "16px",
                        borderRadius: 1,
                        mb: "1px",
                        backgroundColor: item.color || "#888",
                        border: "1px solid rgba(255,255,255,0.6)",
                    }}
                />
                <span>{item.title}</span>
            </Box>
        ) : (
            item.title
        )}
    </MenuItem>
);

const normalizeStrict = (s) =>
    String(s ?? "")
        .toLowerCase()
        .normalize("NFC");

export default function AxionSelect(props) {
    const {
        name,
        label,
        sx,
        value,
        error = null,
        onChange,
        options = [],
        showNoneOption = true,
        disabled = false,
        multiple = false,
        noneText,
        backgroundColor,
        othersText = "",
        priorityOptionIds = [],
        groupsInitiallyCollapsed = false,
        searchField = false,
        searchFieldPlaceholder = "Sök…",
    } = props;

    const [search, setSearch] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const searchInputRef = React.useRef(null);

    const handleOpen = React.useCallback(() => {
        setOpen(true);

        // När menyn är mountad: fokusera sökrutan
        setTimeout(() => {
            searchInputRef.current?.focus();
            searchInputRef.current?.select?.();
        }, 0);
    }, []);

    const handleClose = React.useCallback(() => {
        setOpen(false);
        setSearch("");
    }, []);

    const hasGroups = React.useMemo(() => {
        return options.some(o => o && o.group);
    }, [options]);

    const normalizedPriorityOptionIds = React.useMemo(() => {
        return (Array.isArray(priorityOptionIds) ? priorityOptionIds : [priorityOptionIds])
            .filter(id => id !== null && id !== undefined && id !== "");
    }, [priorityOptionIds]);

    const priorityOptionIdSet = React.useMemo(() => {
        return new Set(normalizedPriorityOptionIds);
    }, [normalizedPriorityOptionIds]);

    const sectionKeys = React.useMemo(() => {
        if (!hasGroups) return [];

        const keys = new Set();
        let hasUngrouped = false;

        options.forEach(o => {
            if (!o) return;
            if (o.group) {
                keys.add(`group:${o.group}`);
                return;
            }
            hasUngrouped = true;
        });

        if (hasUngrouped) {
            keys.add('__ungrouped');
        }

        return [...keys];
    }, [hasGroups, options]);

    const [collapsedSections, setCollapsedSections] = React.useState(() => {
        const initial = {};
        sectionKeys.forEach(key => {
            initial[key] = groupsInitiallyCollapsed;
        });

        // Om ett värde redan är valt, expandera den gruppen
        if (value != null && value !== "") {
            const selectedValues = Array.isArray(value) ? value : [value];
            selectedValues.forEach(selectedId => {
                const selectedOption = options.find(o => o && o.id === selectedId);
                if (selectedOption && !priorityOptionIdSet.has(selectedOption.id)) {
                    const group = selectedOption.group;
                    const sectionKey = group ? `group:${group}` : '__ungrouped';
                    initial[sectionKey] = false; // expandera denna grupp
                }
            });
        }

        return initial;
    });

    React.useEffect(() => {
        setCollapsedSections(prev => {
            const next = {};
            sectionKeys.forEach(key => {
                next[key] = prev[key] ?? groupsInitiallyCollapsed;
            });

            // Om ett värde redan är valt, expandera den gruppen
            if (value != null && value !== "") {
                const selectedValues = Array.isArray(value) ? value : [value];
                selectedValues.forEach(selectedId => {
                    const selectedOption = options.find(o => o && o.id === selectedId);
                    if (selectedOption && !priorityOptionIdSet.has(selectedOption.id)) {
                        const group = selectedOption.group;
                        const sectionKey = group ? `group:${group}` : '__ungrouped';
                        next[sectionKey] = false; // expandera denna grupp
                    }
                });
            }

            return next;
        });
    }, [sectionKeys, groupsInitiallyCollapsed, value, options, priorityOptionIdSet]);

    const toggleSection = React.useCallback((sectionKey) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !(prev[sectionKey] ?? groupsInitiallyCollapsed),
        }));
    }, [groupsInitiallyCollapsed]);

    const filteredOptions = React.useMemo(() => {
        if (!searchField) return options;

        const rawQuery = String(search ?? "").trim();
        if (!rawQuery) return options;

        const q = normalizeStrict(rawQuery);
        if (!q) return options;

        return options.filter(o => {
            if (!o) return false;

            // valfritt: låt även gruppnamn matcha
            const hay = normalizeStrict(`${o.title ?? ""} ${o.group ?? ""}`);
            return hay.includes(q);
        });
    }, [options, search, searchField]);

    const isSearchActive = React.useMemo(() => {
        return searchField && String(search ?? "").trim().length > 0;
    }, [searchField, search]);

    const firstMatchId = React.useMemo(() => {
        // första item i filtrerade options som går att välja
        const first = (filteredOptions || []).find(o => o && o.id !== undefined && o.id !== null && !o.disabled);
        return first ? first.id : null;
    }, [filteredOptions]);

    const selectValue = React.useCallback((selectedId) => {
        if (selectedId === null || selectedId === undefined) return;

        if (!multiple) {
            onChange?.({
                target: { name, value: selectedId }
            });
            handleClose(); // stäng menyn (din interna close)
            return;
        }

        // multiple: toggla selectedId i arrayen
        const current = Array.isArray(value) ? value : [];
        const exists = current.includes(selectedId);
        const next = exists ? current.filter(x => x !== selectedId) : [...current, selectedId];

        onChange?.({
            target: { name, value: next }
        });

        // vid multiple kan du välja att INTE stänga.
        // Om du vill stänga ändå:
        handleClose();
    }, [multiple, onChange, name, value, handleClose]);

    const menuNodes = React.useMemo(() => {
        const opts = filteredOptions;

        const prioritizedIds = normalizedPriorityOptionIds;

        const prioritizedIdSet = new Set(prioritizedIds);
        const prioritizedById = new Map((opts || []).filter(o => o && prioritizedIdSet.has(o.id)).map(o => [o.id, o]));
        const prioritizedOptions = prioritizedIds
            .map(id => prioritizedById.get(id))
            .filter(Boolean);

        const optionsWithoutPrioritized = (opts || []).filter(o => o && !prioritizedIdSet.has(o.id));

        const nodes = [];

        prioritizedOptions.forEach(item => nodes.push(renderOption(item, false)));

        if (!hasGroups) {
            optionsWithoutPrioritized.forEach(item => nodes.push(renderOption(item, false)));
            return nodes;
        }

        const grouped = {};
        const ungrouped = [];

        optionsWithoutPrioritized.forEach(o => {
            if (o && o.group) {
                if (!grouped[o.group]) grouped[o.group] = [];
                grouped[o.group].push(o);
            } else if (o) {
                ungrouped.push(o);
            }
        });

        const sortItems = (a, b) => {
            const ao = (a && a.order !== undefined && a.order !== null) ? a.order : Number.POSITIVE_INFINITY;
            const bo = (b && b.order !== undefined && b.order !== null) ? b.order : Number.POSITIVE_INFINITY;
            if (ao !== bo) return ao - bo;

            const at = String(a?.title ?? '');
            const bt = String(b?.title ?? '');
            return at.localeCompare(bt, 'sv');
        };

        const groupNames = Object.keys(grouped).sort((a, b) => {
            const aItems = grouped[a] || [];
            const bItems = grouped[b] || [];

            const aMin = Math.min(
                ...aItems.map(x =>
                    (x && x.groupOrder !== undefined && x.groupOrder !== null) ? x.groupOrder : Number.POSITIVE_INFINITY
                )
            );

            const bMin = Math.min(
                ...bItems.map(x =>
                    (x && x.groupOrder !== undefined && x.groupOrder !== null) ? x.groupOrder : Number.POSITIVE_INFINITY
                )
            );

            if (aMin !== bMin) return aMin - bMin;
            return a.localeCompare(b, 'sv');
        });

        groupNames.forEach(groupName => {
            const items = [...(grouped[groupName] || [])].sort(sortItems);
            if (items.length === 0) return; // (säkerhet) visa inte tom grupp

            const sectionKey = `group:${groupName}`;
            const isCollapsed = isSearchActive ? false : (collapsedSections[sectionKey] ?? groupsInitiallyCollapsed);

            nodes.push(
                <ListSubheader
                    key={`__group_${groupName}`}
                    component="div"
                    disableSticky
                    sx={{
                        opacity: 1,
                        color: "rgba(0,0,0,0.65)",
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        lineHeight: "normal",
                        py: 1,
                        px: 0,
                        bgcolor: "rgba(0,0,0,0.04)",
                        borderTop: "1px solid rgba(0,0,0,0.10)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSection(sectionKey);
                        }}
                        sx={{
                            width: "100%",
                            border: 0,
                            p: 0,
                            m: 0,
                            bgcolor: "transparent",
                            color: "inherit",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 2,
                            py: 0,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            lineHeight: "inherit",
                            letterSpacing: "inherit",
                            textTransform: "inherit",
                        }}
                    >
                        {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <span>{groupName}</span>
                    </Box>
                </ListSubheader>
            );

            if (!isCollapsed) {
                items.forEach(item => nodes.push(renderOption(item, true)));
            }
        });

        if (ungrouped.length > 0) {
            const ungroupedSectionKey = '__ungrouped';
            const isUngroupedCollapsed = isSearchActive ? false : (collapsedSections[ungroupedSectionKey] ?? groupsInitiallyCollapsed);

            nodes.push(
                <ListSubheader
                    key="__group_other"
                    component="div"
                    disableSticky
                    sx={{
                        opacity: 1,
                        color: "rgba(0,0,0,0.65)",
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        lineHeight: "normal",
                        py: 1,
                        px: 0,
                        bgcolor: "rgba(0,0,0,0.04)",
                        borderTop: "1px solid rgba(0,0,0,0.10)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSection(ungroupedSectionKey);
                        }}
                        sx={{
                            width: "100%",
                            border: 0,
                            p: 0,
                            m: 0,
                            bgcolor: "transparent",
                            color: "inherit",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 2,
                            py: 0,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            lineHeight: "inherit",
                            letterSpacing: "inherit",
                            textTransform: "inherit",
                        }}
                    >
                        {isUngroupedCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <span>{othersText || "Övriga"}</span>
                    </Box>
                </ListSubheader>
            );

            if (!isUngroupedCollapsed) {
                [...ungrouped].sort(sortItems).forEach(item => nodes.push(renderOption(item, false)));
            }
        }

        return nodes;
    }, [filteredOptions, hasGroups, othersText, collapsedSections, groupsInitiallyCollapsed, toggleSection, normalizedPriorityOptionIds, isSearchActive]);

    const searchHeader = React.useMemo(() => {
        if (!searchField) return null;

        return (
            <ListSubheader
                key="__search"
                disableSticky
                sx={{
                    bgcolor: "white",
                    py: 1,
                    lineHeight: "normal",
                }}
            >
                <TextField
                    size="small"
                    fullWidth
                    value={search}
                    placeholder={searchFieldPlaceholder}
                    inputRef={searchInputRef}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        e.stopPropagation();

                        if (e.key === "Escape") {
                            handleClose();
                        }

                        if (e.key === "Enter") {
                            e.preventDefault();

                            // Om det finns en tydlig första träff => välj den
                            if (firstMatchId != null) {
                                selectValue(firstMatchId);
                            }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "rgba(0,0,0,0.6)" }} fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            backgroundColor: '#f7f7f7',
                            borderRadius: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0,0,0,0.35)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0,0,0,0.6)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#799c4a',
                            borderWidth: 2,
                        },
                        '& input': {
                            color: '#000',
                        },
                        '& input::placeholder': {
                            color: 'rgba(0,0,0,0.6)',
                            opacity: 1,
                        },
                    }}
                />
            </ListSubheader>
        );
    }, [searchField, search, searchFieldPlaceholder, handleClose, firstMatchId, selectValue]);


    return (
        <FormControl fullWidth sx={sx} variant="outlined" error={!!error}>
            {label && (
                <InputLabel
                    sx={{
                        '&.Mui-focused': { color: "#799c4a" },
                        '&.MuiFormLabel-root.MuiInputLabel-shrink': { top: '15px' },
                        color: "#799c4a",
                    }}
                >
                    {label}
                </InputLabel>
            )}

            <MuiSelect
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                variant="filled"
                size="small"
                label={label}
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                disabled={disabled}
                multiple={multiple}
                MenuProps={{
                    disableAutoFocusItem: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                        sx: {
                            bgcolor: 'white',
                            color: 'black',
                            '& .MuiMenuItem-root': { py: 1 },
                            maxHeight: 420,
                        },
                    },
                }}
                sx={{
                    '& .MuiSvgIcon-root': { color: 'black' },
                    '&.MuiFilledInput-root': {
                        overflow: 'hidden',
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: '#799c4a',
                        background: disabled ? 'gray' : backgroundColor || '#ffffff',
                        color: 'black'
                    },
                    ...(!label ? {
                    '& .MuiInputBase-input ': {
                        padding: '12px',
                    }
                    } : {})
                }}
            >
                {searchHeader}

                {showNoneOption && <MenuItem value="">{noneText || "None"}</MenuItem>}

                {menuNodes.length > 0 ? (
                    menuNodes
                ) : (
                    <MenuItem disabled value="__no_results" sx={{ opacity: 1, color: "rgba(0,0,0,0.55)" }}>
                        Inga träffar
                    </MenuItem>
                )}
            </MuiSelect>

            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
}
